import { useState } from 'react'

import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import { OverviewPageFeatureAvailabilityFragment } from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import type { Company } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

const buildDashboardFilters = (company: Company, interval: { startDate: Date; endDate: Date }) => {
  return {
    endDate: moment(interval.endDate).format('YYYY-MM-DD'),
    startDate: moment(interval.startDate).format('YYYY-MM-DD'),
    currency: company?.legalEntities.find((el) => el.main)?.currency ?? 'EUR',
    companyId: company.id,
  }
}

export const useOverviewPage = (featureAvailability?: OverviewPageFeatureAvailabilityFragment) => {
  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const [moduleUpsellBannerHidden, setModuleUpsellBannerHidden] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.hideFinanceOverviewModuleUpsellBanner,
    false
  )

  const [interval, setInterval] = useState({
    startDate: new Date(moment().subtract(30, 'days').format('YYYY-MM-DD')),
    endDate: new Date(moment().format('YYYY-MM-DD')),
  })

  const expensesAvailable =
    featureAvailability?.featureAvailabilitiesConnection?.nodes.find(
      (node) => node.id == Features.expenses_core
    )?.available ?? false
  const procurementAvailable =
    featureAvailability?.featureAvailabilitiesConnection?.nodes.find(
      (node) => node.id == Features.procurement_core
    )?.available ?? false
  const softwareAvailable =
    featureAvailability?.featureAvailabilitiesConnection?.nodes.find(
      (node) => node.id == Features.it_management_management
    )?.available ?? false
  const accountsPayableAvailable =
    featureAvailability?.featureAvailabilitiesConnection?.nodes.find(
      (node) => node.id == Features.accounts_payable_management
    )?.available ?? false

  const modulesOrderedByPriority = [
    {
      module: AvailableModules.expenses,
      feature: Features.expenses_core,
      available: expensesAvailable,
    },
    {
      module: AvailableModules.procurement,
      feature: Features.accounts_payable_management,
      available: accountsPayableAvailable,
    },
    {
      module: AvailableModules.procurement,
      feature: Features.procurement_core,
      available: procurementAvailable,
    },
    {
      module: AvailableModules.it_management,
      feature: Features.it_management_management,
      available: softwareAvailable,
    },
  ]

  const missingModule = modulesOrderedByPriority.find((module) => module['available'] == false)

  const onDateRangeChange = (startDate: Date, endDate: Date) => {
    setInterval({ startDate, endDate })
    track(trackingEvents.accounting.overview.dateNavigationFilterChanged, {
      start_date: startDate.toISOString(),
      end_date: endDate.toISOString(),
    })
  }

  const dashboardFilters = buildDashboardFilters(company, interval)

  return {
    moduleUpsellBannerHidden,
    setModuleUpsellBannerHidden,
    interval,
    missingModule,
    onDateRangeChange,
    dashboardFilters,
    locale,
  }
}
